import { useNavigate } from "react-router-dom";
import useErrorHandler from "../FormErrorHandler";
import {
  TextInput,
  RadioInput,
  FormInputRow,
  FormSection,
  DateInput,
  FormHeader,
  PhoneNumberInput,
  AddressInput,
} from "../../FormInput/FormInput";
import { validateZipcode } from "../Validations";
import { useEffect } from "react";

const GettingStarted = ({
  formData,
  handleInputChange,
  validateForm,
  onBack,
}) => {
  const { error, addError, clearError, hasError } = useErrorHandler();
  const navigate = useNavigate();

  const validate = async (formData) => {
    clearError();

    if (!formData.lossInfo.DateOfLoss) {
      addError({ DateOfLoss: "Loss Date is required" });
    }

    if (!formData.lossInfo.TimeOfLoss) {
      addError({ TimeOfLoss: "Loss Time is required" });
    }

    if (!formData.user.FirstName) {
      addError({ FirstName: "First name is required" });
    }

    if (!formData.user.LastName) {
      addError({ LastName: "Last name is required" });
    }

    if (!formData.involvedDriverIndex) {
      addError({ involvedDriverIndex: "Involved driver is required" });
    }

    if (!formData.involvedVehicleIndex) {
      addError({ involvedVehicleIndex: "Involved vehicle is required" });
    }

    if(formData.user.ZipCode){
      if (isNaN(parseInt(formData.user.ZipCode)) || formData.user.ZipCode.toString().length != 5)
        addError({ ZipCode: "Enter a 5 digit zip code" });
      else {
        const state = await validateZipcode(formData.user.ZipCode)
        if(!state)
          addError({ ZipCode: "Enter a valid zip code" });
        else
          handleInputChange("State", state, "user");
      } 
    }

    return !hasError();
  };

  validateForm.current = validate;
  onBack.current = () => {
    navigate("/home");
    return true;
  };

  const handleDriverChange = (fieldName, value, parentField) => {
    handleInputChange(fieldName, value, parentField)
    if (value != "other") {
      handleInputChange("cureDriverInfo", formData.drivers[parseInt(value)])
    }
    else {
      handleInputChange("cureDriverInfo", {})
    }
  }

  const handleVehicleChange = (fieldName, value, parentField) => {
    handleInputChange(fieldName, value, parentField)
    if(value != "other"){
      handleInputChange("vehicleInfo", formData.vehicles[parseInt(value)])
    }
    else{
      handleInputChange("vehicleInfo", {})
    }
  }

  return (
    <div>
      <FormHeader>GETTING STARTED</FormHeader>
      <FormSection header={"Let's get started. Tell us what happened."}>
        <FormInputRow header="Loss Date*" sameLine={true}>
          <DateInput
            value={formData.lossInfo.DateOfLoss}
            handleInputChange={handleInputChange}
            fieldName={"DateOfLoss"}
            props={{
              placeholderText: "Select Date",
              maxDate: new Date(),
              dateFormat: "MM/dd/yyyy"
            }}
            parentField="lossInfo"
            error={error}
            addError={addError}
          />
        </FormInputRow>

        <FormInputRow header="Loss Time*" sameLine={true}>
          <DateInput
            value={formData.lossInfo.TimeOfLoss}
            handleInputChange={handleInputChange}
            fieldName={"TimeOfLoss"}
            mode="time"
            props={{
              placeholderText: "Select Time",
              showTimeSelect: true,
              showTimeSelectOnly: true,
              timeIntervals: 15,
              timeCaption: "Time",
              dateFormat: "h:mm aa",
            }}
            parentField="lossInfo"
            error={error}
          />
        </FormInputRow>
      </FormSection>

      <FormSection header={"Your Information"}>
        <FormInputRow header="Who is reporting this loss?*">
          <TextInput
            fieldName="FirstName"
            value={formData.user.FirstName}
            handleInputChange={handleInputChange}
            placeholder="First Name"
            error={error}
            parentField="user"
          />
          <TextInput
            fieldName="LastName"
            value={formData.user.LastName}
            handleInputChange={handleInputChange}
            placeholder="Last Name"
            error={error}
            parentField="user"
          />
        </FormInputRow>

        <FormInputRow header="Relationship to the Policyholder">
          <TextInput
            fieldName="RelationToPolicyHolder"
            value={formData.user.RelationToPolicyHolder}
            handleInputChange={handleInputChange}
            parentField="user"
          />
        </FormInputRow>

        <FormInputRow header="Phone Number">
          <PhoneNumberInput
            fieldName="PhoneNumber"
            value={formData.user.PhoneNumber}
            handleInputChange={handleInputChange}
            parentField="user"
          />
        </FormInputRow>

        <FormInputRow header="Email">
          <TextInput
            fieldName="EmailAddress"
            value={formData.user.EmailAddress}
            handleInputChange={handleInputChange}
            parentField="user"
          />
        </FormInputRow>

        <FormInputRow header="Home Address">
          <AddressInput
            addressData={formData.user}
            handleInputChange={handleInputChange}
            parentField="user"
            error={error}
          />
        </FormInputRow>
      </FormSection>

      <FormSection header="Who was the driver involved in the loss?*">
        <FormInputRow>
          <RadioInput
            fieldName="involvedDriverIndex"
            value={formData.involvedDriverIndex}
            handleInputChange={handleDriverChange}
            error={error}
            type="radio"
            options={[
              ...formData.drivers.map((data, index) => {
                data.index = index;
                return {
                  value: `${index}`,
                  label: (
                    <div>
                      <span className="fw-bold">{data.fullName}</span> - DOB (
                      {data.DOB})
                    </div>
                  ),
                };
              }),
              { value: "other", label: "Other Driver" },
            ]}
          />
        </FormInputRow>
      </FormSection>

      <FormSection header="Which vehicle was involved in this loss?*">
        <FormInputRow>
          <RadioInput
            fieldName="involvedVehicleIndex"
            value={formData.involvedVehicleIndex}
            handleInputChange={handleVehicleChange}
            error={error}
            type="radio"
            options={[
              ...formData.vehicles.map((data, index) => {
                return {
                  value: `${index}`,
                  label: (
                    <div>
                      <span className="fw-bold">
                        {data.modelyr} {data.manufacturer} {data.model}
                      </span>{" "}
                      - ({data.vehidentificationnumber})
                    </div>
                  ),
                };
              }),
              { value: "other", label: "Other Vehicle" },
            ]}
          />
        </FormInputRow>
      </FormSection>
    </div>
  );
};

export default GettingStarted;
